<script lang="ts" setup>
import TagsSelectorOption from './TagsSelectorOption.vue'
import { useContainerTagsStore } from '~/stores/containerTags'
const props = withDefaults(
  defineProps<{
    /** The Tags Array, -1 means "not tagged" is selected */
    modelValue: string[]
    noTagsModelValue?: boolean
    placeholder?: string
    showNoTagsOption?: boolean
    hideValues?: boolean // Useful if you display selected values elsewhere
    allowCreate?: boolean
  }>(),
  {
    placeholder: 'Select tags',
    showNoTagsOption: false,
    hideValues: false,
    allowCreate: true,
  }
)
const emit = defineEmits<{
  /** Emit Tags Array back to parent  */
  (e: 'update:modelValue', value: string[]): void
  (e: 'update:noTagsModelValue', value: boolean): void
  (e: 'close', value: string | undefined): void
  (e: 'valueSearched', value: string | undefined): void
  (e: 'tagDeleted', tag: string): void
  (e: 'tagRenamed', update: { oldTag: string; newTag: string }): void
}>()

const selectedTags = computed({
  get: () => {
    const value: (string | -1)[] = props.modelValue || []
    if (props.noTagsModelValue) {
      return [...value, -1] as string[]
    }
    return value
  },
  set: (newTags: (string | -1)[]) => {
    const filterToNotTagged = newTags.includes(-1)
    const tagsWithoutNotTagged = [...newTags.filter((t) => t !== -1)]
    if (filterToNotTagged !== props.noTagsModelValue) {
      emit('update:noTagsModelValue', filterToNotTagged)
    }
    if (tagsWithoutNotTagged.length !== props.modelValue.length) {
      emit('update:modelValue', tagsWithoutNotTagged)
    }
  },
})
const containerTagsStore = useContainerTagsStore()
const options = computed(() => {
  const options: { label: string; value: string | -1 }[] = []
  if (props.showNoTagsOption) {
    options.push({
      label: 'Untagged',
      value: -1,
    })
  }
  options.push(
    ...[...containerTagsStore.tags].sort().map((tag) => ({
      label: tag,
      value: tag,
    }))
  )
  return options
})
type SelectRefElement = HTMLElement | null
const selectRef: Ref<SelectRefElement | null> = ref(null)

interface InputEvent extends Event {
  target: HTMLInputElement
}

const clearMultiSelectInput = async (event: InputEvent) => {
  event.target.value = '' // Clear the input
  emit('valueSearched', undefined)

  // Attempt to close the dropdown by blurring the input field if possible
  if (selectRef.value) {
    // @ts-expect-error
    selectRef.value.query = '' // This helps to reset the internal state or query
  }

  await nextTick() // give DOM time to update

  if (selectRef.value) {
    // @ts-expect-error
    const input = selectRef.value.$el.querySelector('input')

    // this will allow to trigger the dopdown with definedTags
    if (input) {
      input.blur()
      input.focus()
    }
  }
}

const onKeyupEnterAddTag = async (event: KeyboardEvent) => {
  if (!event.target) return // Should never happen
  const target = event.target as HTMLInputElement
  if (target.value) {
    emit('valueSearched', target.value.trim() || undefined)
  } else {
    emit('valueSearched', undefined)
  }
  if (event.key === 'Enter') {
    await clearMultiSelectInput({ target } as InputEvent)
  }
}

function onTagDeleted(tag: string) {
  selectedTags.value = selectedTags.value.filter((t) => t !== tag)
  emit('tagDeleted', tag)
}
function onTagRenamed(update: { oldTag: string; newTag: string }) {
  selectedTags.value = selectedTags.value.map((t) =>
    t === update.oldTag ? update.newTag : t
  )
  emit('tagRenamed', update)
}
</script>

<template>
  <el-select-v2
    ref="selectRef"
    v-model="selectedTags"
    :options="options"
    aria-label="Select tags"
    multiple
    :placeholder="props.placeholder"
    :allow-create="props.allowCreate"
    filterable
    size="large"
    :class="{ 'hide-values': props.hideValues }"
    :default-first-option="true"
    :fit-input-width="250"
    @keyup="onKeyupEnterAddTag"
  >
    <template #prefix>
      <slot name="prefix" />
    </template>
    <template #default="{ item }">
      <div
        v-if="item.value === -1"
        role="option"
        class="w-full flex justify-between"
      >
        <div class="italic">Untagged</div>
        <Help title="Untagged filter" style="margin-right: -7px">
          Show only containers that have not been tagged
        </Help>
      </div>
      <TagsSelectorOption
        v-else
        :tag="item.label"
        @tag-deleted="onTagDeleted"
        @tag-renamed="onTagRenamed"
      />
    </template>
  </el-select-v2>
</template>

<style scoped lang="scss">
.hide-values {
  :deep(
    .el-select__wrapper
      div.el-select__selected-item:not(.el-select__input-wrapper)
  ) {
    display: none;
  }
}
</style>
